<template>
    <form class="flex flex-col"
          :class="[`tr-multistep-form-step-${currentStep}`]"
          ref="form"
          @keydown.enter.prevent>
        <input type="hidden" name="page" :value="currentPage">
        <input v-for="(value, name) in trackingParams" type="hidden" :key="name" :name="name" :value="value">
        <div class="flex flex-col lg:grid lg:grid-cols-12 lg:gap-8">
            <!-- left side with form -->
            <div class="order-2 lg:order-1 lg:col-start-1 lg:col-end-7">
                <div v-if="success || errorMessage"
                     class="relative bg-white rounded-2xl border-2 min-h-38 px-5 pt-13 pb-7 -mx-5 mt-20 mb-12 lg:mt-15 lg:pl-18 lg:pr-8 lg:py-9 lg:mr-0 lg:ml-13"
                     :class="errorMessage ? 'border-red' : 'border-green'">
                    <div class="absolute left-1/2 -top-7 -translate-x-1/2 flex justify-center items-center w-14 h-14 transform rotate-45 rounded-mid
                                lg:-left-11 lg:top-1/2 lg:w-22 lg:h-22 lg:translate-x-0 lg:-translate-y-1/2 lg:rounded-3xl"
                         :class="errorMessage ? 'bg-red' : 'bg-green'">
                        <icon :name="errorMessage ? 'close' : 'check'" class="fill-current w-6 h-6 transform -rotate-45" />
                    </div>
                    <slot v-if="errorMessage" name="error"/>
                    <slot v-else name="thankyou"/>
                </div>
                <template v-else>
                    <div class="mt-16 lg:mt-5">
                        <div class="flex flex-col gap-2 items-start">
                            <div class="text-sm h-5">{{ requestLabel }} <span class="text-sm h-5">*</span></div>
                            <div class="flex flex-wrap lg:flex-row 2xl:flex-nowrap gap-2.5 lg:gap-3">
                                <template v-for="(option, i) in firstStepOptions">
                                    <component :is="getOptionTag(i)"
                                               :href="getSupportClaimLink(i)"
                                               :target="getSupportClaimTarget(i)"
                                               :key="`1-${i}`"
                                               @click="select(i)"
                                               class="cursor-pointer flex gap-5 lg:gap-3  rounded-md py-4 px-3 lg:py-1 lg:pl-1 lg:pr-8 group overflow-hidden border transition-colors duration-300 " :class="[!valid && !selectedOption ?'border-red border-opacity-100' : 'border-light-gray', selectedOption === i  ?'bg-white  text-black border-opacity-100' : 'hover:text-black hover:bg-white text-white border-opacity-50']">
                                        <div
                                            class="hidden lg:flex items-center transition-colors duration-300 text-white rounded-mid lg:rounded-md p-5 lg:p-3" :class="selectedOption === i ? 'bg-red group-hover:bg-dark-red' : 'group-hover:text-black'">
                                            <icon :name="getIcon(i)" class="w-6 lg:w-4 h-6 lg:h-4 fill-current"/>
                                        </div>
                                        <div class="flex flex-col justify-center font-bold uppercase font-heading pt-1 leading-3 flex-nowrap whitespace-nowrap">
                                            {{ option }}
                                        </div>
                                    </component>
                                </template>
                            </div>
                            <div v-if="!valid && !selectedOption" class="text-xs text-right" :class="{'text-red' : !contrast }">{{ requiredLabel }}</div>
                        </div>
                        <!--<div v-show="showFrame" class="col-span-2 row-span-6 py-8">
                            <iframe class="w-full h-200" :src="supportClaimLink" ref="iframe"/>
                        </div>-->
                        <div>
                            <input type="hidden" :value="selectedOption" name="purpose" id="purpose">
                            <slot name="contact"/>
                            <div class="mt-14 lg:mt-4 mb-11 lg:mb-15">
                                <slot name="customer" :toggle="toggleCustomer" :type="isCustomer ? 'businessclient' : 'businessnoclient'"/>
                                <slot name="marketing"/>
                                <slot name="privacy" :toggle="toggle"/>
                                <slot v-if="showSustainabilityCheckbox" name="sustainability"/>
                            </div>
                        </div>
                        <slot name="company-size-note"/>
                        <div class="group flex lg:justify-end cursor-pointer mt-8 lg:mt-14">
                            <button class="button group-hover:bg-light-red"
                                    type="submit"
                                    id="submit-form"
                                    @click.prevent="submit">
                                {{ submitLabel }}
                            </button>
                            <button class="button py-3 -ml-px group-hover:bg-light-red"
                                    type="submit"
                                    @click.prevent="submit">
                                <icon name="mail" class="stroke-current w-4 h-4 mx-px lg:group-hover:animate-bounce"/>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
            <!-- right side with custom content -->
            <div class="order-1 lg:order-2 lg:col-start-8 lg:col-end-13">
                <slot name="right"/>
                <h4 class="text-h3 lg:text-h1 text-center lg:text-left">
                    <slot name="headline"/>
                </h4>
            </div>
        </div>
    </form>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Ref } from 'vue-property-decorator';
import Icon from '../base/Icon.vue';
import axios from 'axios';
import Helper from '../../libs/Helper';

const FORM_ENDPOINT = '/.rest/api/v2/form';
const TRACKING_PARAMS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term'];

@Component({
    components: { Icon }
})
export default class MultiStepForm extends Vue {
    @Prop({ required: true }) firstStepOptions: Record<string, string>;
    @Prop() supportClaimLink: string;
    @Prop({ required: true }) submitLabel: string;
    @Prop({ required: true }) responseLabel: string;
    @Prop({ required: true }) requestLabel: string;
    @Prop({ required: true }) requiredLabel: string;
    @Prop({ default: false, type: Boolean }) showSustainabilityCheckbox: boolean;
    @Prop({ default: false, type: Boolean }) contrast: boolean;

    @Ref() form: HTMLFormElement;

    selectedOption = ''
    valid = true
    success = false;
    checked = false;
    isCustomer = false;
    loading = false;
    errorMessage = false;

    select(option: string) {
        if (!this.supportClaimLink || (option !== 'supportclaim')) {
            if (this.selectedOption === option) {
                this.selectedOption = '';
            } else {
                this.selectedOption = option;
            }
        }
    }

    async submit() {
        this.errorMessage = false;
        this.loading = true;
        this.valid = true;
        const form: HTMLFormElement = this.form;
        const fd: FormData = new FormData(form);

        if (!fd.has('customertype')) {
            fd.set('customertype', 'businessnoclient');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const encodedFd: URLSearchParams = new URLSearchParams(fd as any); // this works but type checking fails without `any`

        try {
            if (form.checkValidity() && this.selectedOption) {
                const res = await axios({
                    method: 'post',
                    url: `${this.$contextPath}${FORM_ENDPOINT}/${this.$site}/${this.$lang}`,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    data: encodedFd
                });
                // send form submit event to gtm
                if (window.dataLayer) {
                    const dataLayer = window.dataLayer || [];
                    dataLayer.push({
                        event: 'formSubmit',
                        purpose: fd.get('purpose'),
                        productinterest: fd.get('productinterest'),
                        customertype: fd.get('customertype')
                    });
                }
                if (res.data.success) {
                    this.success = true;
                } else {
                    this.errorMessage = true;
                }
                this.loading = false;
            } else {
                this.valid = false;
                for (let i = 0; i < form.elements.length; i++) {
                    // trigger html validation messages
                    if (!(form.elements[i] as HTMLFormElement).checkValidity()) {
                        (form.elements[i] as HTMLFormElement).dataset.touched = '';
                        this.loading = false;
                        // break;
                    }
                }
            }
        } catch (err) {
            this.errorMessage = true;
            this.loading = false;
        }
    }

    toggle() {
        this.checked = !this.checked;
    }

    toggleCustomer() {
        this.isCustomer = !this.isCustomer;
    }

    getTrackingParams(params: URLSearchParams) {
        return TRACKING_PARAMS.reduce((obj, x) => {
            if (params.has(x)) {
                obj[x] = params.get(x);
            }
            return obj;
        }, {});
    }

    getOptionTag(option: string): string {
        return this.isShowSupportLink(option) ? 'a' : 'div';
    }

    getSupportClaimLink(option: string): string {
        return this.isShowSupportLink(option) ? this.supportClaimLink : null;
    }

    getSupportClaimTarget(option: string): string {
        return this.isShowSupportLink(option) ? '_blank' : null;
    }

    isShowSupportLink(option: string): boolean {
        return !!this.supportClaimLink && (option === 'supportclaim');
    }

    getIcon(option: string): string {
        switch (option) {
            case 'productoffer':
                return 'brand';
            case 'businessclient':
                return 'folder-check';
            case 'businessnoclient':
                return 'folder-xmark';
            case 'supportclaim':
            default:
                return 'support';
        }
    }

    get currentPage(): string {
        return location.pathname + location.search;
    }

    get trackingParams(): Record<string, string> {
        const cookie = Helper.getCookie('lzcp');
        if (cookie) {
            const params = new URLSearchParams(cookie.replace(/^.*\?/, ''));
            const cookieParams = this.getTrackingParams(params);
            const urlParams = this.getTrackingParams(new URLSearchParams(location.search));
            return { ...cookieParams, ...urlParams };
        }
        return {};
    }
}
</script>
